import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Row, Modal, Badge, Table, ProgressBar, Nav, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PieChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from 'providers/appProvider';
import axios from 'axios';
import moment from 'moment';
import { fetchRoleAndClaims, handleLoadingAndAccess, handleSendProposal, notifyLeaveDecision, fetchLeaveConflictMap } from "../../assets/js/common";
import { toast } from "react-toastify";
import paths from 'routes/paths';
import { faUmbrellaBeach, faBriefcaseMedical, faFileAlt, faPlus, faExclamationCircle, faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, Link } from "react-router-dom";
import IconButton from "components/common/iconButton";
import CreateOrderDrawer from "components/calender/createOrderDrawer";
import useSocket from '../../hooks/useSocket';
import USER_ROLES from 'config/roles.config';

const MySwal = withReactContent(Swal);
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    PieChart,
    CanvasRenderer,
    LegendComponent
]);

const getDateRange = () => {
    return {
        startDate: moment(0),
        endDate: moment().add(100, 'years')
    };
};

// Starter Plan Benefits
const starterPlanBenefits = [
    "starterPlanBenefits.accessBasicFeatures",
    "starterPlanBenefits.customBranding",
    "starterPlanBenefits.emailNotifications"
];

const BarSegment = ({ pct, bg, label, borderRadius }) => {
    const ref = useRef(null);
    const [showText, setShowText] = useState(true);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            const width = el.offsetWidth;
            const textWidth = pct.length * 8;
            setShowText(width >= textWidth + 8);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [pct]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`bar-tooltip-${label}`}>{label}: {pct}</Tooltip>}
        >
            <div
                ref={ref}
                className={`d-flex align-items-center justify-content-center text-white fw-medium font-size-12 overflow-hidden text-nowrap ${bg}`}
                style={{ width: pct, borderRadius }}>
                {showText ? pct : ""}
            </div>
        </OverlayTrigger>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { userLocation, changeLocation, locations, isEmployeeView, setIsEmployeeView } = useAppContext();
    const [isLoading, setIsLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const dashboardStartTimeRef = useRef(null);
    const apiTimingsRef = useRef({});
    const { t, i18n } = useTranslation();
    const userRole = localStorage.getItem("UserRole");
    const currentUserId = localStorage.getItem("UserId");
    const currentCustomerId = localStorage.getItem("customerId");
    const currentLocationId = localStorage.getItem("LocationId");
    const [forecastTab, setForecastTab] = useState("Workforce");
    const [weeklyLeaves, setWeeklyLeaves] = useState([]);
    const [conflictMap, setConflictMap] = useState({});
    const [openProposals, setOpenProposals] = useState([]);
    const [newReviews, setNewReviews] = useState([]);
    const [newComplaints, setNewComplaints] = useState([]);
    const [outdatedVersionOrders, setOutdatedVersionOrders] = useState([]);
    const [versionActionLoadingId, setVersionActionLoadingId] = useState(null);
    const [sendingProposalId, setSendingProposalId] = useState(null);
    const [decisionLoading, setDecisionLoading] = useState(null);
    const [data, setData] = useState(null);
    const [roles, setRoles] = useState([]);
    const [adminRoleId, setAdminRoleId] = useState(null);
    const [appOwnerRoleId, setAppOwnerRoleId] = useState(null);
    const [offerData, setOfferData] = useState([]);
    const [planningDrawer, setPlanningDrawer] = useState({
        show: false,
        clientId: null,
        clientName: "",
    });

    const [dashboardStats, setDashboardStats] = useState({
        relations: { total: 0, percentage: 0, trend: "up" },
        activeOrders: { total: 0, percentage: 0, trend: "up" },
        ordersToday: { total: 0, percentage: 0, trend: "up" },
        workforce: { total: 0, percentage: 0, trend: "up" },
        taskStatus: { planned: 0, inProgress: 0, completed: 0, delayed: 0 }
    });

    const getWeekRange = () => {
        const today = moment();
        const startDate = today.clone().startOf("week").add(1, "day"); // Monday
        const endDate = startDate.clone().add(6, "days"); // Sunday
        return {
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD")
        };
    };

    const fetchWeeklyLeaves = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            const { startDate, endDate } = getWeekRange();

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/unavailabilityByCustomerIdWithDate`,
                {
                    params: { customerId, startDate, endDate },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );

            const leaves = res.data || [];
            const userIds = [...new Set(leaves.map(l => l.UserId).filter(Boolean))];

            if (!userIds.length) {
                setWeeklyLeaves([]);
                return;
            }

            const usersRes = await axios.post(
                `${process.env.REACT_APP_API_URL}/userRoute/users/byIds`,
                { userIds },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            const users = usersRes.data || [];
            const userMap = {};
            users.forEach(u => { userMap[u.UserId] = u.Name; });

            const mergedLeaves = leaves.map(l => ({
                ...l,
                UserName: userMap[l.UserId] || t("unknownText")
            }));

            setWeeklyLeaves(mergedLeaves);

        } catch (error) {
            console.error("Error fetching weekly leaves:", error);
            setWeeklyLeaves([]);
        }
    };

    const fetchConflictMap = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            const { startDate, endDate } = getDateRange(); 
            const map = await fetchLeaveConflictMap(
                customerId,
                startDate.format("YYYY-MM-DD"),
                endDate.format("YYYY-MM-DD")
            );
            setConflictMap(map);
        } catch (error) {
            console.error("Error fetching conflict map:", error);
            setConflictMap({});
        }
    };

    const today = moment().startOf("day");
    const weekStart = moment().startOf("isoWeek");
    const weekEnd = moment().endOf("isoWeek");

    /* sick leave shown only if active today */
    const sickLeaves = weeklyLeaves.filter(item => {
        const start = moment(item.StartDate);
        const end = moment(item.EndDate);
        return (
            item.Type === "sickness" &&
            item.Status === "approved" &&
            start.isSameOrBefore(today) &&
            end.isSameOrAfter(today)
        );
    });

    const formatLeaveRange = (leave) => {
        const start = moment(leave.StartDate);
        const end = moment(leave.EndDate);

        const formatDate = (m) => m.format("DD-MM-YYYY");
        const formatDateShort = (m) => m.format("DD-MM-YYYY");

        const startDateFull = formatDate(start);
        const startDateShort = formatDateShort(start);
        const endDateShort = formatDateShort(end);
        const endYear = end.format("YYYY");

        let startTime = null;
        let endTime = null;

        if (leave.StartTime && moment(leave.StartTime).isValid()) {
            const time = moment.utc(leave.StartTime).format("HH:mm");
            const combined = moment.utc(`${leave.StartDate} ${time}`, "YYYY-MM-DD HH:mm");
            if (combined.isValid()) startTime = combined.local().format("HH:mm");
        }

        if (leave.EndTime && moment(leave.EndTime).isValid()) {
            const time = moment.utc(leave.EndTime).format("HH:mm");
            const combined = moment.utc(`${leave.EndDate} ${time}`, "YYYY-MM-DD HH:mm");
            if (combined.isValid()) endTime = combined.local().format("HH:mm");
        }

        if (start.isSame(end, "day")) {
            if (startTime && endTime) return `${startDateFull} | ${startTime} - ${endTime}`;
            return startDateFull;
        }

        if (startTime && endTime) return `${startDateShort} - ${endDateShort} ${endYear} | ${startTime} - ${endTime}`;
        return `${startDateShort} - ${endDateShort} ${endYear}`;
    };

    const pendingVacations = weeklyLeaves.filter(item => {
        const start = moment(item.StartDate);
        return (
            item.Type === "vacation" &&
            item.Status === "pending" &&
            start.isBetween(weekStart, weekEnd, null, "[]")
        );
    });

    const fetchOutdatedVersionOrders = async () => {
        try {
            const locationId = userLocation?.LocationId || currentLocationId;
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderRoute/outdatedVersionOrders`,
                {
                    params: { customerId: currentCustomerId, locationId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );
            setOutdatedVersionOrders(res.data || []);
        } catch (error) {
            console.error("Error fetching outdated version orders:", error);
        }
    };

    const handleUpdateOrderVersionFromDashboard = async (orderId) => {
        setVersionActionLoadingId(`${orderId}-update`);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/orderRoute/${orderId}/updateWithNewProposalVersion`,
                { adminUserId: currentUserId },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            toast.success(t("orderUpdatedToLatestVersion"), { position: "top-right", autoClose: 3000 });
            fetchOutdatedVersionOrders();
        } catch (error) {
            console.error("Failed to update order to new proposal version:", error);
            toast.error( t("orderVersionUpdateFailed"), { position: "top-right", autoClose: 3000 });
        } finally {
            setVersionActionLoadingId(null);
        }
    };

    const handleCancelUrgentOrderItem = async (orderId) => {
        setVersionActionLoadingId(`${orderId}-cancel`);
        try {
            const adminUserId = localStorage.getItem("UserId");
            await axios.post(
                `${process.env.REACT_APP_API_URL}/orderRoute/${orderId}/cancelUrgentAlert`,
                { adminUserId },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setOutdatedVersionOrders((prev) => prev.filter((o) => o.OrderId !== orderId));
        } catch (error) {
            console.error("Failed to cancel urgent alert:", error);
            toast.error(t("orderVersionUpdateFailed"), { position: "top-right", autoClose: 3000 });
        } finally {
            setVersionActionLoadingId(null);
        }
    };

    const handleDeclineOrderVersionFromDashboard = async (orderId) => {
        setVersionActionLoadingId(`${orderId}-decline`);
        try {
            const adminUserId = localStorage.getItem("UserId");
            await axios.post(
                `${process.env.REACT_APP_API_URL}/orderRoute/${orderId}/declineNewProposalVersion`,
                { adminUserId },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            fetchOutdatedVersionOrders();
        } catch (error) {
            console.error("Failed to decline proposal version update:", error);
            toast.error(t("orderVersionUpdateFailed"), { position: "top-right", autoClose: 4000 });
        } finally {
            setVersionActionLoadingId(null);
        }
    };

    const fetchOpenProposals = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/proposalRoute/openProposalsByCustomer`,
                {
                    params: { customerId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );
            setOpenProposals(res.data || []);

        } catch (error) {
            console.error("Error fetching open proposals:", error);

        }
    };

    const fetchNewReviews = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderDetailReviewRoute/newReviewsByCustomer`,
                {
                    params: { customerId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );
            setNewReviews(res.data || []);
        } catch (error) {
            console.error("Error fetching new reviews:", error);
            setNewReviews([]);
        }
    };

    const fetchNewComplaints = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderDetailComplaintRoute/newComplaintsByCustomer`,
                {
                    params: { customerId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );
            setNewComplaints(res.data || []);
        } catch (error) {
            console.error("Error fetching new complaints:", error);
            setNewComplaints([]);
        }
    };

    const handleViewProposalFromDashboard = async (proposal) => {
        try {
            const customerId = localStorage.getItem("customerId");

            const proposalLocationId = proposal?.LocationId;
            if (proposalLocationId) {
                let matchedLocation = locations.find(loc => loc.LocationId === proposalLocationId);

                if (!matchedLocation) {
                    const locRes = await axios.get(
                        `${process.env.REACT_APP_API_URL}/locationRoute/locationById/${proposalLocationId}`,
                        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                    );
                    matchedLocation = locRes.data;
                }

                if (matchedLocation) {
                    localStorage.setItem("LocationId", matchedLocation.LocationId);
                    localStorage.setItem("location", JSON.stringify(matchedLocation));
                    changeLocation(matchedLocation);
                }
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/proposalTemplateSectionsRoute/getProposalTemplates/${customerId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            const templates = response.data || [];
            const template = templates.find((t) => t.TemplateId === proposal.TemplateId);
            const templateLanguage = template?.LanguageCode || "en";

            localStorage.setItem("culture", templateLanguage);

            navigate(`/proposals/${proposal.ProposalId}/details-proposal?culture=${templateLanguage}&openViewer=true`);
        } catch (error) {
            console.error("Error opening proposal:", error);
        }
    };

    const sendProposalFromDashboard = async (proposal) => {
        try {
            setSendingProposalId(proposal.ProposalId);

            const proposalDetailsRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/getProposalDetailsByProposalId/${proposal.ProposalId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            const relationRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/relationRoute/relationByNumber/${proposal.Client}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            const proposalDocumentDetail = proposalDetailsRes.data;
            const relationData = relationRes.data;

            if (!proposalDocumentDetail?.CompanySignature) {
                toast.error(t("companySignatureNotAvailable"), { position: "top-right", autoClose: 2000 });
                return;
            }

            const formData = {
                locationData: relationData,
                ClientEmailaddress: relationData.Emailaddress1
            };

            await handleSendProposal(
                proposalDocumentDetail,
                formData,
                relationData,
                t,
                () => { },
                () => { },
                process.env.REACT_APP_DEFAULT_DOMAIN
            );

            await axios.patch(
                `${process.env.REACT_APP_API_URL}/proposalRoute/updateProposal-status/${proposal.ProposalId}`,
                { Status: 3 },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            fetchOpenProposals();
        } catch (error) {
            console.error("Error sending proposal:", error);
            toast.error(t("proposalSentError"), { position: "top-right", autoClose: 2000 });
        } finally {
            setSendingProposalId(null);
        }
    };

    const fetchDashboardStats = async () => {

        try {
            const customerId = localStorage.getItem("customerId");
            if (!customerId) return;

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/dashboardStatisticsRoute/stats`,
                {
                    params: { customerId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );

            setDashboardStats(res.data);

        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
        }
    };

    useEffect(() => {
        Promise.all([
            fetchDashboardStats(),
            fetchWeeklyLeaves(),
            fetchOpenProposals(),
            fetchOutdatedVersionOrders(),
            fetchConflictMap(),
            fetchNewReviews(),
            fetchNewComplaints()
        ]).then(() => {

        });
    }, []);


    const [isForceUpdate, setIsForceUpdate] = useState(
        sessionStorage.getItem('app_update_required') === 'true'
    );

    useEffect(() => {
        const interval = setInterval(() => {
            setIsForceUpdate(sessionStorage.getItem('app_update_required') === 'true');
        }, 500);
        return () => clearInterval(interval);
    }, []);

    const [blockReason, setBlockReason] = useState(
        localStorage.getItem('subscriptionModalOpen') === 'true' ? 'unknown' : null
    );

    const [customerData, setCustomerData] = useState(null);
    const [topClients, setTopClients] = useState([]);

    const markModalAsShown = () => {
        localStorage.setItem('subscriptionModalShown', 'true');
    };

    const fetchUserData = async (userId) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setData(response.data);
        } catch (error) {
            console.error("Error fetching user data:", error);
        }
    };

    const fetchTopClients = async () => {
        performance.mark("fetchTopClients:start");
        try {
            const customerId = localStorage.getItem("customerId");
            const locationId = userLocation?.LocationId || localStorage.getItem("LocationId");
            if (!customerId || !locationId) return;

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/dashboardStatisticsRoute/newClients`,
                {
                    params: { customerId, locationId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );
            setTopClients(res.data || []);
        } catch (error) {
            console.error("Error fetching top clients:", error);
        }
    };

    useEffect(() => {
        fetchTopClients();
    }, [userLocation?.LocationId]);

    useSocket('orderDetail:updated', () => {
        fetchDashboardStats();
    });

    useSocket('orderDetail:deleted', () => {
        fetchDashboardStats();
    });

    useSocket('order:created', () => {
        fetchDashboardStats();
        fetchTopClients();
    });

    useSocket('orderDetail:created', () => {
        fetchDashboardStats();
        fetchTopClients();
    });

    useSocket('order:updated', () => {
        fetchDashboardStats();
    });

    useSocket('order:deleted', () => {
        fetchDashboardStats();
        fetchTopClients();
    });

    useSocket('relation:created', () => {
        fetchDashboardStats();
        fetchTopClients();
    });

    useSocket('relation:updated', () => {
        fetchDashboardStats();
        fetchTopClients();
    });

    useSocket('relation:deleted', () => {
        fetchDashboardStats();
        fetchTopClients();
    });

    useSocket('unavailability:updated', () => {
        fetchWeeklyLeaves();
    });

    useSocket('review:created', () => {
        fetchNewReviews();
    });

    useSocket('complaint:created', () => {
        fetchNewComplaints();
    });

    // Instantly refresh subscription/offboard status for Admin/App Owner when a super admin offboards this customer, without needing a page refresh
    const refetchCustomerStatus = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            if (!customerId) return;
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setCustomerData(res.data);
        } catch (error) {
            console.error("Error refetching customer status after offboard:", error);
        }
    };

    useSocket('force-logout-non-admin', ({ reason }) => {
        const userRole = localStorage.getItem("UserRole");
        const isAdminOrAppOwner = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER;
        if (isAdminOrAppOwner && reason === "ACCOUNT_OFFBOARDED") {
            refetchCustomerStatus();
        }
    });

    const fetchRoles = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/roleRoute/roles`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setRoles(response.data);

            const adminRole = response.data.find((roles) => roles.Name === USER_ROLES.ADMIN);
            const appOwnerRole = response.data.find((roles) => roles.Name === USER_ROLES.APP_OWNER);

            setAdminRoleId(adminRole?.RoleId || null);
            setAppOwnerRoleId(appOwnerRole?.RoleId || null);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleNotNowWithLogout = () => {
        handleCloseSubscriptionModal();
    };

    useEffect(() => {
        const customerId = localStorage.getItem("customerId");
        const loggedInEmail = localStorage.getItem("UserEmail");
        const userId = localStorage.getItem("UserId");

        if (!customerId || !loggedInEmail || !userId) return;

        fetchUserData(userId);
        fetchRoles();

        const checkSubscriptionStatus = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );

                const customer = res.data;
                setCustomerData(customer);

                if (
                    !customer?.Email ||
                    customer.Email.toLowerCase() !== loggedInEmail.toLowerCase() ||
                    customer?.Subscription?.SubscriptionType !== "Trial"
                ) {
                    return;
                }

                if (!customer?.NextBillingDate) return;

                const today = moment();
                const expiry = moment(customer.NextBillingDate);
                const daysLeft = expiry.diff(today, "days");

                const todayKey = moment().format("YYYY-MM-DD");
                if (localStorage.getItem("subscriptionToastShown") === todayKey) return;

                if (daysLeft < 0) {
                    toast.error(t("yourSubscriptionExpired"), { position: "top-right", autoClose: 7000 });
                } else if (daysLeft === 0) {
                    toast.error(t("yourSubscriptionExpiresToday"), { position: "top-right", autoClose: 7000 });
                } else if (daysLeft > 0 && daysLeft <= 7) {
                    toast.error(t("yourSubscriptionExpiresInNextDays", { daysLeft }), { position: "top-right", autoClose: 7000 });
                }

                localStorage.setItem("subscriptionToastShown", todayKey);
            } catch (error) {
                console.error("Error checking subscription:", error);
            }
        };

        checkSubscriptionStatus();
    }, [t]);

    useEffect(() => {
        if (!customerData) return;

        const userRole = localStorage.getItem("UserRole");
        const nextBillingDate = customerData?.NextBillingDate;
        const subscriptionType = customerData?.Subscription?.SubscriptionType;
        const mollieSubscriptionStatus = customerData?.MollieSubscriptionStatus;
        const isOffboard = customerData?.IsActive === "Offboard";
        const isActive = customerData?.IsActive === "Active";

        const isSubscriptionExpired =
            customerData?.ProviderSubscriptionStatus === "Cancelled" &&
            customerData?.SubscriptionEndDate &&
            moment(customerData.SubscriptionEndDate).isBefore(moment());

        const blockingRules = [
            { reason: "offboard", condition: isOffboard },
            { reason: "paymentRequired", condition: customerData?.PaymentRequired && isActive },
            { reason: "subscriptionExpired", condition: isSubscriptionExpired },
            {
                reason: "trialEnded",
                condition:
                    subscriptionType === "Trial" &&
                    userRole === USER_ROLES.APP_OWNER &&
                    (mollieSubscriptionStatus === "pastDue" ||
                        (nextBillingDate && moment(nextBillingDate).isBefore(moment(), 'day'))),
            },
        ];

        const activeReason = blockingRules.find(r => r.condition)?.reason ?? null;

        setBlockReason(activeReason);
        if (activeReason) markModalAsShown();
    }, [customerData]);

    useEffect(() => {
        if (blockReason) {
            localStorage.setItem('subscriptionModalOpen', 'true');
        } else {
            localStorage.removeItem('subscriptionModalOpen');
        }
    }, [blockReason]);

    const handleCloseSubscriptionModal = () => {
        setBlockReason(null);
        localStorage.removeItem('subscriptionModalOpen');
    };

    const handleUpgradeNow = () => {
        navigate("/subscription/renew-subscription");
        handleCloseSubscriptionModal();
    };

    useEffect(() => {
        const fetchOffers = async () => {
            try {
                setDataLoading(true);
                if (!userLocation?.LocationId) return;

                const customerId = localStorage.getItem("customerId");

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/proposalRoute/getProposals`,
                    {
                        params: { locationId: userLocation.LocationId, customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                    }
                );

                const sortedData = response.data.sort((a, b) => {
                    const dateA = new Date(a.updatedAt || a.createdAt);
                    const dateB = new Date(b.updatedAt || b.createdAt);
                    return dateB - dateA;
                });

                setOfferData(sortedData);

            } catch (error) {
                console.error("Error fetching proposals:", error);

            } finally {
                setDataLoading(false);
            }
        };

        fetchOffers();
    }, [userLocation?.LocationId]);

    useEffect(() => {
        fetchRoleAndClaims({
            navigate,
            claimType: "adminDashboard",
            claimValue: "view",
            setHasAccess,
            setIsLoading,
        });
    }, [navigate]);

    useEffect(() => {
        let handleOnboardingDismissed = null;
        
        const checkSession = () => {
            const isLoggedIn = localStorage.getItem("isLoggedIn");
            if (!isLoggedIn) MySwal.close();
        };

        const interval = setInterval(checkSession, 1000);
        const userRole = localStorage.getItem("UserRole");
        const isMedewerker = userRole === USER_ROLES.EMPLOYEE;

        if (!isMedewerker) {
            const missingSignature = localStorage.getItem("missingSignature") === "true";
            const missingEmailConfig = localStorage.getItem("missingEmailConfig") === "true";
            const emailConfigInvalid = localStorage.getItem("emailConfigInvalid") === "true";

            const fields = [];
            if (missingSignature) fields.push(t("missingSignature"));
            if (missingEmailConfig) fields.push(t("missingEmailConfig"));

            const isSubscriptionActive = customerData?.NextBillingDate &&
                moment(customerData.NextBillingDate).isAfter(moment(), 'day');
            const isEnterprise = customerData?.Subscription?.SubscriptionType === "Enterprise";

            const isOffboard = customerData?.IsActive === "Offboard";

            const showIncompleteProfileAlert = () => {
                if (fields.length > 0 && !isOffboard && !customerData?.PaymentRequired && (isSubscriptionActive || isEnterprise)) {
                    MySwal.fire({
                        title: t("incompleteProfileTitle"),
                        html: `${fields.join("<br>")}<br><br>${t("wouldYouLikeToAddIt")}`,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: t("goToProfile"),
                        cancelButtonText: t("continueToDashboard"),
                        reverseButtons: true,
                        allowOutsideClick: false
                    }).then((result) => {
                        if (result.isConfirmed) navigate("/profile");
                    });

                    localStorage.removeItem("missingSignature");
                    localStorage.removeItem("missingEmailConfig");
                }
            };

            const hasCompletedTourGuide = localStorage.getItem("hasCompletedTourGuide") === "true";
            if (!hasCompletedTourGuide) {
                handleOnboardingDismissed = () => {
                    window.removeEventListener("onboarding:dismissed", handleOnboardingDismissed);
                    showIncompleteProfileAlert();
                };
                window.addEventListener("onboarding:dismissed", handleOnboardingDismissed);
            } else {
                showIncompleteProfileAlert();
            }

            if (emailConfigInvalid) {
                toast.warn(
                    <div className="d-flex align-items-start gap-2">
                        <i className="fas fa-exclamation-triangle text-warning mt-1 flex-shrink-0" />
                        <span>{t("emailConfigInvalid")}</span>
                    </div>,
                    {
                        position: "top-right",
                        autoClose: 8000,
                        toastId: "email-config-invalid",
                        icon: false,
                        className: "bg-warning-subtle text-warning border-start border-warning border-4",
                        progressClassName: "bg-warning",
                    
                    }
                );
                localStorage.removeItem("emailConfigInvalid");
            }
        }

        return () => {
            clearInterval(interval);
            if (handleOnboardingDismissed) {
                window.removeEventListener("onboarding:dismissed", handleOnboardingDismissed);
            }
        };
    }, [navigate, t, customerData]);

    const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);
    const goToEmployeeDashboard = () => {
        sessionStorage.setItem("isEmployeeView", "true");
        setIsEmployeeView(true);
        navigate("/employee/dashboard");
    };

    const handleCreatePlanning = (client) => {
        setPlanningDrawer({
            show: true,
            clientId: client.relationNumber || client.RelationNumber || null,
            clientName: client.relationName || client.RelationName || client.Name || "",
        });
    };

    if (loadingOrRedirect) return loadingOrRedirect;

    if (dataLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">{t("loading")}</span>
                </div>
            </div>
        );
    }

    const getTranslatedShortMonth = (date) => {
        const monthKey = moment(date).format("MMMM").toLowerCase();
        return t(`shortMonths.${monthKey}`);
    };

    const calculateDuration = (start, end) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = diffTime / (1000 * 60 * 60 * 24) + 1;
        return t("day", { count: diffDays });
    };

    const formatLocalTime = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return "--";
        const time = moment.utc(timeStr).format("HH:mm:ss");
        const combinedUtc = moment.utc(`${moment.utc(dateStr).format("YYYY-MM-DD")}T${time}`);
        return combinedUtc.local().format("HH:mm");
    };

    const handleDecision = async (unavailabilityId, newStatus, note = null) => {
        setDecisionLoading(`${unavailabilityId}-${newStatus}`);
        try {
            const adminId = localStorage.getItem("UserId");
            const customerId = localStorage.getItem("customerId");
            const decisionDate = moment().format("YYYY-MM-DD");

            const payload = {
                status: newStatus,
                decisionOn: decisionDate,
                decisionBy: adminId,
                adminNote: note || null
            };

            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/updateStatus/${unavailabilityId}`,
                payload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            if (response.status !== 200) throw new Error("Update failed");

            toast.success(
                newStatus === "approved" ? t("leaveApprovedSuccessfully") : t("leaveRejectedSuccessfully"),
                { position: "top-right", autoClose: 2000 }
            );

            await fetchWeeklyLeaves();

            // ── Notification — fire-and-forget, routed through the centralized dispatcher ──
            notifyLeaveDecision({
                unavailabilityId,
                updatedRecord: response.data,
                newStatus,
                note,
                adminId,
                customerId,
                t,
            }).catch(err => console.error("Leave decision notification error (non-blocking):", err));

        } catch (error) {
            console.error("Decision error:", error);
            toast.error(
                newStatus === "approved" ? t("failedToApproveLeave") : t("failedToRejectLeave"),
                { position: "top-right", autoClose: 2000 }
            );
        } finally {
            setDecisionLoading(null);
        }
    };

    const getTaskStatusBarSegments = () => {
        const ts = dashboardStats.taskStatus || {};
        const total = (ts.planned || 0) + (ts.inProgress || 0) + (ts.completed || 0) + (ts.delayed || 0);
        if (total === 0) return [];
        return [
            { key: "planned", bg: "bg-primary", label: t("planned"), count: ts.planned || 0 },
            { key: "inProgress", bg: "bg-info", label: t("inProgress"), count: ts.inProgress || 0 },
            { key: "completed", bg: "bg-success", label: t("completed"), count: ts.completed || 0 },
            { key: "delayed", bg: "bg-danger", label: t("delayed"), count: ts.delayed || 0 },
        ]
            .filter(s => s.count > 0)
            .map(s => ({ ...s, pct: `${Math.round((s.count / total) * 100)}%` }));
    };

    const statsCards = [
        { icon: "fas fa-handshake", label: t("activeClients"), value: dashboardStats.relations.total.toString(), change: `${dashboardStats.relations.percentage}%`, positive: dashboardStats.relations.trend === "up", color: "text-primary", bg: "bg-primary-subtle", path: "/relations" },
        { icon: "fas fa-clipboard-list", label: t("activeOrdersLabel"), value: dashboardStats.activeOrders.total.toString(), change: `${dashboardStats.activeOrders.percentage}%`, positive: dashboardStats.activeOrders.trend === "up", color: "text-primary", bg: "bg-primary-subtle", path: "/orders" },
        { icon: "fas fa-receipt", label: t("ordersToday"), value: dashboardStats.ordersToday.total.toString(), change: `${dashboardStats.ordersToday.percentage}%`, positive: dashboardStats.ordersToday.trend === "up", color: "text-warning", bg: "bg-warning-subtle" },
        { icon: "fas fa-users", label: t("workforceOnSite"), value: dashboardStats.workforce.total.toString(), change: `${dashboardStats.workforce.percentage}%`, positive: dashboardStats.workforce.trend === "up", color: "text-primary", bg: "bg-primary-subtle", path: "/planning" },
    ];

    const subscriptionModalContentMap = {
        offboard: { title: t("activateYourAccount"), message: t("offboardActivateSubscription") },
        paymentRequired: { title: t("timeToSubscribe"), message: t("subscriptionRequiredMessage") },
        subscriptionExpired: { title: t("subscriptionExpiredTitle"), message: t("subscriptionExpiredMessage") },
        trialEnded: { title: t("timeToUpgrade"), message: t("trialEndedUpgrade") },
    };

    const subscriptionModalContent = subscriptionModalContentMap[blockReason] || subscriptionModalContentMap.trialEnded;

    return (
        <>
            {/* Subscription Expired Modal - Hidden for Enterprise users */}
            {!isForceUpdate && blockReason && (
                <Modal
                    show={!!blockReason}
                    onHide={() => { }}
                    centered
                    size="sm"
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Header className="border-0 pb-0 position-relative">
                        <div className="position-absolute top-0 start-50 translate-middle">
                            <span className="badge bg-warning px-3 py-2 rounded-pill">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                {t("paymentRequired")}
                            </span>
                        </div>
                    </Modal.Header>

                    <Modal.Body className="text-center px-4 pb-3">
                        <div className="text-warning mb-3">
                            <i className="fas fa-crown fa-3x"></i>
                        </div>

                        <h5 className="fw-bold text-dark mb-2">
                            {subscriptionModalContent.title}
                        </h5>

                        <p className="text-muted small mb-3">
                            {subscriptionModalContent.message}
                        </p>

                        <div className="bg-light rounded-2 p-3 mb-3">
                            {starterPlanBenefits.map((benefitKey, index) => (
                                <div key={index} className="d-flex align-items-center mb-2">
                                    <i className="fas fa-check text-success me-2 small"></i>
                                    <span className="small">{t(benefitKey)}</span>
                                </div>
                            ))}
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="border-0 pt-0">
                        <div className="d-grid gap-2 w-100">
                            <Button
                                variant="primary"
                                onClick={handleUpgradeNow}
                                className="rounded-2 py-2"
                                size="sm"
                            >
                                <i className="fas fa-rocket me-2"></i>
                                {t("upgradeNow")}
                            </Button>

                            {blockReason === "trialEnded" &&  (
                                <Button
                                    variant="outline-dark"
                                    onClick={handleNotNowWithLogout}
                                    className="rounded-2 py-2"
                                    size="sm"
                                >
                                    {t("notNow")}
                                </Button>
                            )}
                        </div>
                    </Modal.Footer>
                </Modal>
            )}

            <Row className="g-3 mb-3">
                <Col lg={12} xxl={12}>
                    <Card className="mb-3 h-100">
                        <Background
                            image={corner4}
                            className="bg-card"
                            style={{
                                borderTopRightRadius: "0.375rem",
                                borderBottomRightRadius: "0.375rem",
                            }}
                        />
                        <Card.Body className="position-relative pe-4 pb-2">
                            <Row className="align-items-center flex-nowrap">
                                <Col>
                                    <h5 className="mb-0">{t("dashboard")}</h5>
                                </Col>
                                <Col xs="auto" className="flex-shrink-0">
                                    {(userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={goToEmployeeDashboard}
                                        >
                                            {t("employeeDashboard")}
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Stats Row */}
            <Row className="g-3 mb-3">
                {statsCards.map((s, i) => (
                    <Col xs={6} sm={6} xl={3} key={i}>
                        <Card
                            className="h-100 flex-grow-0 shadow-none rounded-3 card-hover border"
                            onClick={() => s.path && navigate(s.path)}
                            style={s.path ? { cursor: "pointer" } : {}}
                        >
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className={`d-flex align-items-center justify-content-center rounded-2 stat-icon ${s.bg}`}>
                                        <i className={`${s.icon} fs-8 ${s.color}`}></i>
                                    </div>
                                    <span className={`badge rounded ${s.positive ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
                                        <i className={`fas ${s.positive ? "fa-arrow-up" : "fa-arrow-down"} me-1`}></i>
                                        {String(s.change).replace(/[+-]/, "")}
                                    </span>
                                </div>
                                <span className="mb-1 fw-medium font-size-12 d-block">
                                    {s.label}
                                </span>
                                <span className="mb-0 fw-bolder font-size-22">
                                    {s.value}
                                </span>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="g-3">
                <Col lg={8}>
                    {/* Order Health Status */}
                    <Card className="flex-grow-0 shadow-none rounded-3 mb-3 card-hover border">
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h3 className="mb-0 fw-medium font-size-15">{t("orderHealthStatus")}</h3>
                                <div className="d-grid d-sm-flex flex-sm-wrap gap-1 gap-sm-2 font-size-13 text-secondary fw-normal"
                                    style={{ gridTemplateColumns: '1fr 1fr' }}>
                                    {[["bg-primary", t("planned")], ["bg-info", t("inProgress")], ["bg-success", t("completed")], ["bg-danger", t("delayed")]].map(([bg, label]) => (
                                        <span key={label} className="d-flex align-items-center gap-1">
                                            <span className={`rounded-circle d-inline-block flex-shrink-0 status-dot ${bg}`}></span>
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <span className="fw-normal text-muted font-size-13">{t("today")}</span>
                                <div className="d-flex flex-1 rounded-pill w-100 progress-bar-wrap">
                                    {getTaskStatusBarSegments().map(({ key, pct, bg, label }, index, arr) => {
                                        const isFirst = index === 0;
                                        const isLast = index === arr.length - 1;
                                        const borderRadius = `${isFirst ? '50px' : '0'} ${isLast ? '50px' : '0'} ${isLast ? '50px' : '0'} ${isFirst ? '50px' : '0'}`;
                                        return <BarSegment key={key} pct={pct} bg={bg} label={label} borderRadius={borderRadius} />;
                                    })}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* New Clients */}
                    <Card className="flex-grow-0 shadow-none rounded-3 mb-3 card-hover border d-flex flex-column top-clients-card">
                        <Card.Body className="p-3 flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="mb-0 fw-medium font-size-15">{t("newClients")}</h3>
                            </div>
                            <div className="table-responsive scrollbar overflow-auto table-scroll-container">
                                <Table borderless className="mb-0 font-size-13 text-nowrap w-100">
                                    <thead>
                                        <tr className="text-500 fw-medium font-size-11">
                                            <th className="pb-2 ps-0 bg-100">{t("clientName")}</th>
                                            <th className="pb-2 text-start bg-100 w-25">{t("activeOrders")}</th>
                                            <th className="pb-2 text-start bg-100 w-25">{t("workforce")}</th>
                                            <th className="pb-2 text-start bg-100 w-25">{t("status")}</th>
                                            <th className="pb-2 text-start bg-100 w-25">{t("action")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topClients.map((client, i) => {
                                            const initial = client.relationName?.charAt(0)?.toUpperCase() || "?";
                                            const colors = [
                                                { bg: "bg-primary-subtle", text: "text-primary" },
                                                { bg: "bg-info-subtle", text: "text-info" },
                                                { bg: "bg-warning-subtle", text: "text-warning" },
                                                { bg: "bg-success-subtle", text: "text-success" },
                                            ];
                                            const color = colors[i % colors.length];
                                            return (
                                                <tr key={client.relationId} className="border-top border-200 align-middle">
                                                    <td className="py-3 ps-0">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className={`rounded-2 d-flex align-items-center justify-content-center fw-bold font-size-13 client-initial ${color.bg} ${color.text}`}>
                                                                {initial}
                                                            </div>
                                                            <span className="fw-medium text-900 font-size-13">{client.relationName}</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 text-start text-700 font-size-13">{client.activeOrders}</td>
                                                    <td className="py-3 text-start text-700 font-size-13">{client.totalEmployees} {t("assigned")}</td>
                                                    <td className="py-3 text-start text-700 font-size-13">{t("noStatusAvailable")}</td>

                                                    <td className="py-3 text-start">
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => handleCreatePlanning(client)}
                                                        >
                                                            {t("newPlanning")}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {topClients.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted py-3 font-size-13">{t("NoDataFound")}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Next 7 Days Forecast */}
                    <Card className="flex-grow-0 shadow-none rounded-3 card-hover border">
                        <Card.Body className="p-3 forecast-card-body">
                            <div className="d-flex justify-content-between mb-3 gap-2 align-items-start align-items-sm-center flex-column-reverse flex-sm-row">
                                <h3 className="mb-0 fw-medium font-size-15 text-nowrap text-start">{t("nextSevenDaysForecast")}</h3>
                                <Nav variant="pills" className="rounded-3 p-1 nav-bg-soft flex-nowrap align-self-start align-self-sm-center">
                                    {[["Workforce", t("workforce")], ["Inventory", t("inventoryForecast")]].map(([key, label]) => (
                                        <Nav.Item key={key}>
                                            <Nav.Link
                                                onClick={() => setForecastTab(key)}
                                                active={forecastTab === key}
                                                className="rounded-3 fw-medium font-size-13 py-1 px-3 px-sm-3 text-muted"
                                            >
                                                {label}
                                            </Nav.Link>
                                        </Nav.Item>
                                    ))}
                                </Nav>
                            </div>
                            <div className="d-flex align-items-center justify-content-center h-100 forecast-no-data">
                                <p className="text-500 fw-medium font-size-13 mb-0">{t("NoDataFound")}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column */}
                <Col lg={4} className="d-flex flex-column">
                    <Card className="flex-grow-0 shadow-none rounded-3 mb-3 overflow-hidden border d-flex flex-column urgent-action-card">
                        <div className="px-3 pt-3 pb-2 bg-100 border-bottom border-200">
                            <div className="d-flex align-items-start">
                                <i className="fas fa-bullhorn text-danger me-2 mt-1 font-size-15"></i>
                                <div>
                                    <h3 className="mb-0 fw-medium font-size-15">{t("urgentActionCenter")}</h3>
                                    <p className="text-muted d-block fw-normal font-size-12">{t("requiresImmediateAttention")}</p>
                                </div>
                            </div>
                        </div>
                        <Card.Body className="p-3 scrollbar perfect-scrollbar urgent-card-body">
                            {/* Sick Leave */}
                            {sickLeaves.map((leave) => (
                                <div key={leave.UnavailabilityId} className="mb-3 p-3 rounded-3 card-hover shadow-none urgent-card-red">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className="fw-medium text-danger font-size-13 d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faBriefcaseMedical} className="text-danger" />
                                            {t("sickLeave")}
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge rounded-pill fw-medium font-size-11 ${leave.Status?.toLowerCase() === "pending" ? "bg-danger text-white" : "bg-success text-white"}`}>
                                                {t(leave.Status?.toLowerCase())}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="mb-1 fw-bold text-900 font-size-13">{leave.UserName || leave.UserId}</p>
                                    <p className="mb-1 fw-medium text-900 font-size-12">{formatLeaveRange(leave)}</p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            onClick={() => navigate(`/leave-management?unavailabilityId=${leave.UnavailabilityId}`)}
                                        >
                                            {t("view")}
                                        </Button>
                                        <Button size="sm" variant="danger" className="fw-medium border-0 font-size-13 rounded-2 w-50" disabled>
                                            {t("replace")}
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {/* Pending Leave Approval */}
                            {pendingVacations.map((leave) => (
                                <div key={leave.UnavailabilityId} className="mb-3 p-3 rounded-3 card-hover shadow-none urgent-card-yellow">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className="fw-medium text-warning font-size-13 d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faUmbrellaBeach} className="text-warning" />
                                            {t("vacationLeave")}
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge rounded-pill fw-medium font-size-11 ${leave.Status?.toLowerCase() === "pending" ? "bg-warning text-white" : "bg-success text-white"}`}>
                                                {t(leave.Status?.toLowerCase())}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="mb-1 fw-bold text-900 font-size-13">{leave.UserName || leave.UserId}</p>
                                    <p className="mb-1 fw-medium text-900 font-size-12">{formatLeaveRange(leave)}</p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            disabled={decisionLoading === `${leave.UnavailabilityId}-rejected`}
                                            onClick={() => handleDecision(leave.UnavailabilityId, "rejected")}
                                        >
                                            {t("reject")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="fw-medium border-0 font-size-13 rounded-2 bg-warning w-50"
                                            disabled={decisionLoading === `${leave.UnavailabilityId}-approved`}
                                            onClick={() => handleDecision(leave.UnavailabilityId, "approved")}
                                        >
                                            {t("approve")}
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {/* Outdated Proposal Version - affects live order */}
                            {outdatedVersionOrders.map((order) => (
                                <div key={order.OrderId} className="mb-3 p-3 rounded-3 card-hover shadow-none urgent-card-yellow">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className="fw-medium text-warning font-size-13 d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faExclamationCircle} className="text-warning" />
                                            {t("orderProposalVersionChanged")}
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge rounded-pill fw-medium font-size-11 bg-warning text-white">
                                                {t("actionRequired")}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="mb-1 fw-bold text-900 font-size-13">{order.ClientName}</p>
                                    <p className="mb-1 fw-medium text-900 font-size-12">{order.OrderNumber} | {order.TemplateName}</p>
                                    <div className="d-flex gap-2 mt-3">
                                        {/* <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            disabled={versionActionLoadingId === `${order.OrderId}-decline`}
                                            onClick={() => handleDeclineOrderVersionFromDashboard(order.OrderId)}
                                        >
                                            {t("decline")}
                                        </Button> */}
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            disabled={versionActionLoadingId === `${order.OrderId}-cancel`}
                                            onClick={() => handleCancelUrgentOrderItem(order.OrderId)}
                                        >
                                            {t("cancel")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="warning"
                                            className="fw-medium border-0 font-size-13 rounded-2 w-50"
                                            disabled={versionActionLoadingId === `${order.OrderId}-update`}
                                            onClick={() => handleUpdateOrderVersionFromDashboard(order.OrderId)}
                                        >
                                            {versionActionLoadingId === `${order.OrderId}-update` ? t("updating") : t("updateOrder")}
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {/* Open Proposals */}
                            {openProposals.map((proposal) => (
                                <div key={proposal.ProposalId} className="mb-3 p-3 rounded-3 card-hover shadow-none urgent-card-yellow">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className="fw-medium text-warning font-size-13 d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faFileAlt} className="text-warning" />
                                            {t("sendProposalPending")}
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge rounded-pill fw-medium font-size-11 bg-success text-white">
                                                {t("open")}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="mb-1 fw-bold text-900 font-size-13">{proposal.ClientName}</p>
                                    <p className="mb-1 fw-medium text-500 font-size-12">{proposal.TemplateName}</p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            onClick={() => handleViewProposalFromDashboard(proposal)}
                                        >
                                            {t("view")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="fw-medium border-0 font-size-13 rounded-2 bg-warning w-50"
                                            onClick={() => sendProposalFromDashboard(proposal)}
                                            disabled={sendingProposalId === proposal.ProposalId}
                                        >
                                            {sendingProposalId === proposal.ProposalId ? t("sending") : t("sendProposal")}
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {/* New Reviews */}
                            {newReviews.map((review) => (
                                <div key={review.OrderDetailReviewId} className="mb-3 p-3 rounded-3 card-hover shadow-none urgent-card-yellow">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className="fw-medium text-warning font-size-13 d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faStar} className="text-warning" />
                                            {t("newReviewReceived")}
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge rounded-pill fw-medium font-size-11 bg-warning text-white">
                                                {review.Rating} ★
                                            </span>
                                        </div>
                                    </div>
                                        <p className="mb-1 fw-bold text-900 font-size-13">{review.CompanyName || review.ClientName}</p>
                                        {review.ContactPerson && (
                                        <p className="mb-1 fw-normal text-600 font-size-12">{review.ContactPerson}</p>
                                        )}
                                        <p className="mb-1 fw-medium text-900 font-size-12">{review.TaskName}</p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            onClick={() => {
                                                axios.patch(
                                                    `${process.env.REACT_APP_API_URL}/orderDetailReviewRoute/markAsSeen/${review.OrderDetailReviewId}`,
                                                    {},
                                                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                                                ).catch(() => {});
                                                setNewReviews((prev) => prev.filter((r) => r.OrderDetailReviewId !== review.OrderDetailReviewId));
                                            }}
                                        >
                                            {t("markAsSeen")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="warning"
                                            className="fw-medium border-0 font-size-13 rounded-2 w-50"
                                            onClick={() => {
                                                axios.patch(
                                                    `${process.env.REACT_APP_API_URL}/orderDetailReviewRoute/markAsSeen/${review.OrderDetailReviewId}`,
                                                    {},
                                                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                                                ).catch(() => {});
                                                setNewReviews((prev) => prev.filter((r) => r.OrderDetailReviewId !== review.OrderDetailReviewId));

                                                const navState = {
                                                    openTaskId: review.OrderDetailId,
                                                    openReview: true,
                                                    taskDate: review.TaskDate
                                                };
                                                sessionStorage.setItem("pendingNavState", JSON.stringify(navState));
                                                navigate("/planning", { state: navState });
                                            }}
                                        >
                                            {t("view")}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {/* New Complaints */}
                            {newComplaints.map((complaint) => (
                                <div key={complaint.OrderDetailComplaintId} className="mb-3 p-3 rounded-3 card-hover shadow-none urgent-card-red">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <span className="fw-medium text-danger font-size-13 d-flex align-items-center gap-1">
                                            <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" />
                                            {t("newComplaintReceived")}
                                        </span>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge rounded-pill fw-medium font-size-11 bg-danger text-white">
                                                {t("actionRequired")}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="mb-1 fw-bold text-900 font-size-13">{complaint.ClientName}</p>
                                    <p className="mb-1 fw-medium text-900 font-size-12">{complaint.TaskName}</p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="fw-medium font-size-13 rounded-2 w-50"
                                            onClick={() => {
                                                axios.patch(
                                                    `${process.env.REACT_APP_API_URL}/orderDetailComplaintRoute/markAsSeen/${complaint.OrderDetailComplaintId}`,
                                                    {},
                                                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                                                ).catch(() => {});
                                                setNewComplaints((prev) => prev.filter((c) => c.OrderDetailComplaintId !== complaint.OrderDetailComplaintId));
                                            }}
                                        >
                                            {t("markAsSeen")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            className="fw-medium border-0 font-size-13 rounded-2 w-50"
                                            onClick={() => {
                                                axios.patch(
                                                    `${process.env.REACT_APP_API_URL}/orderDetailComplaintRoute/markAsSeen/${complaint.OrderDetailComplaintId}`,
                                                    {},
                                                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                                                ).catch(() => {});
                                                setNewComplaints((prev) => prev.filter((c) => c.OrderDetailComplaintId !== complaint.OrderDetailComplaintId));

                                                const navState = {
                                                    openTaskId: complaint.OrderDetailId,
                                                    openComplaint: true,
                                                    taskDate: complaint.TaskDate
                                                };
                                                sessionStorage.setItem("pendingNavState", JSON.stringify(navState));
                                                navigate("/planning", { state: navState });
                                            }}
                                        >
                                            {t("view")}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>

                    {/* Quick Action Buttons */}
                    <Row className="g-2">
                        <Col xs={6}>
                            <Button
                                as={Link}
                                to={paths.newOrder}
                                variant="outline-secondary"
                                className="rounded-3 shadow-none bg-transparent border-secondary action-btn view-proposal-btn btn btn-falcon-default btn-sm w-100 d-flex align-items-center justify-content-center fw-medium font-size-13 py-2 px-2">
                                {t("newOrder")}
                            </Button>
                        </Col>
                        <Col xs={6}>
                            <Button
                                as={Link}
                                to={paths.newProposal}
                                variant="outline-secondary"
                                className="rounded-3 shadow-none bg-transparent border-secondary action-btn view-proposal-btn btn btn-falcon-default btn-sm w-100 d-flex align-items-center justify-content-center fw-medium font-size-13 py-2 px-2">
                                {t("newProposal")}
                            </Button>
                        </Col>
                        <Col xs={6}>
                            <Button
                                as={Link}
                                to={paths.newRelation}
                                variant="outline-secondary"
                                className="rounded-3 shadow-none bg-transparent border-secondary action-btn view-proposal-btn btn btn-falcon-default btn-sm w-100 d-flex align-items-center justify-content-center fw-medium font-size-13 py-2 px-2">
                                {t("newrelation")}
                            </Button>
                        </Col>
                        <Col xs={6}>
                            <Button
                                as={Link}
                                to={paths.newUser}
                                variant="outline-secondary"
                                className="rounded-3 shadow-none bg-transparent border-secondary action-btn view-proposal-btn btn btn-falcon-default btn-sm w-100 d-flex align-items-center justify-content-center fw-medium font-size-13 py-2 px-2">
                                {t("newUser")}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <CreateOrderDrawer
                open={planningDrawer.show}
                onClose={() =>
                    setPlanningDrawer({
                    show: false,
                    clientId: null,
                    clientName: "",
                    })
                }
                onCreated={() => {
                    setPlanningDrawer({
                    show: false,
                    clientId: null,
                    clientName: "",
                    });
                    fetchTopClients();
                }}
                preSelectedClientId={planningDrawer.clientId}
                preSelectedClientName={planningDrawer.clientName}
                selectedTimeRange={{ start: null, end: null }}
                conflictMap={conflictMap}
                leaveEvents={weeklyLeaves}
                fetchAllLocations={true}
                t={t}
            />
        </>
    );
};

export default Dashboard;